/**
 * Chat SSE Service for Frontend
 * Manages EventSource connections for real-time chat streaming
 */

export interface ChatStreamEvent {
  type: 'connected' | 'response-start' | 'token' | 'rag-info' | 'reset' | 'complete' | 'error' | 'session-title';
  data: any;
}

export interface ChatSSECallbacks {
  onToken?: (text: string, conversationId: string) => void;
  onStart?: (conversationId: string) => void;
  onRAGInfo?: (chunksRetrieved: number) => void;
  /**
   * The primary model failed partway through and a different one is re-answering.
   * Discard every token received so far for this message; a complete answer follows.
   */
  onReset?: (conversationId: string, reason: string) => void;
  onConnect?: () => void;
  onError?: (error: string) => void;
  onComplete?: (sessionId: string, conversationId: string, sessionTitle?: string) => void;
  onSessionTitle?: (sessionId: string, title: string) => void;
}

class ChatSSEService {
  private connections: Map<string, EventSource>;
  private reconnectAttempts: Map<string, number>;
  private readonly RECONNECT_DELAY = 3000;
  private readonly MAX_RECONNECT_ATTEMPTS = 2;

  constructor() {
    this.connections = new Map();
    this.reconnectAttempts = new Map();
  }

  /**
   * Connect to chat SSE stream
   */
  connect(
    streamSessionId: string,
    callbacks: ChatSSECallbacks
  ): EventSource {
    const connectionKey = `chat_${streamSessionId}`;

    // Close existing connection if any
    this.disconnect(connectionKey);

    // Get API base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    const baseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;
    const url = `${baseUrl}/api/chat/stream/${streamSessionId}`;

    console.log(`[CHAT_SSE] Connecting to: ${url}`);

    const eventSource = new EventSource(url);

    // Handle connection event
    eventSource.addEventListener('connected', (e: MessageEvent) => {
      console.log(`[CHAT_SSE] Connected to chat stream`);
      try {
        const data = JSON.parse(e.data);
        console.log(`[CHAT_SSE] Connection confirmed:`, data.message);
        if (callbacks.onConnect) {
          callbacks.onConnect();
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing connected event:', error);
      }
    });

    // Handle response start
    eventSource.addEventListener('response-start', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.log(`[CHAT_SSE] Response started:`, data);
        if (callbacks.onStart) {
          callbacks.onStart(data.conversationId);
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing response-start event:', error);
      }
    });

    // Handle token chunks
    eventSource.addEventListener('token', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (callbacks.onToken) {
          callbacks.onToken(data.text, data.conversationId);
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing token event:', error);
      }
    });

    // Handle a mid-stream provider switch: everything streamed so far is void.
    eventSource.addEventListener('reset', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.warn('[CHAT_SSE] Stream reset, answer is being regenerated:', data.reason);
        if (callbacks.onReset) {
          callbacks.onReset(data.conversationId, data.reason);
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing reset event:', error);
      }
    });

    // Handle RAG info
    eventSource.addEventListener('rag-info', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (callbacks.onRAGInfo) {
          callbacks.onRAGInfo(data.chunksRetrieved);
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing rag-info event:', error);
      }
    });

    // Handle completion
    eventSource.addEventListener('complete', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.log(`[CHAT_SSE] Stream completed:`, data);
        if (callbacks.onComplete) {
          callbacks.onComplete(data.sessionId, data.conversationId, data.sessionTitle);
        }
        setTimeout(() => {
          this.disconnect(connectionKey);
        }, 1000);
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing complete event:', error);
      }
    });

    // Handle session title (generated async after first real message)
    eventSource.addEventListener('session-title', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (callbacks.onSessionTitle) {
          callbacks.onSessionTitle(data.sessionId, data.title);
        }
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing session-title event:', error);
      }
    });

    // Handle errors
    eventSource.addEventListener('error', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.error(`[CHAT_SSE] Stream error:`, data);
        if (callbacks.onError) {
          callbacks.onError(data.error || data.message || 'Unknown error');
        }
        this.disconnect(connectionKey);
      } catch (error) {
        console.error('[CHAT_SSE] Error parsing error event:', error);
      }
    });

    // Handle connection open
    eventSource.onopen = () => {
      console.log(`[CHAT_SSE] Connection opened`);
      this.reconnectAttempts.set(connectionKey, 0);
    };

    // Handle connection errors - NO automatic reconnection for chat (causes duplicates)
    eventSource.onerror = (error) => {
      console.error(`[CHAT_SSE] Connection error:`, error);
      
      if (eventSource.readyState === EventSource.CLOSED) {
        console.error(`[CHAT_SSE] Connection closed unexpectedly`);
        // Don't reconnect - just report error and let user retry
        if (callbacks.onError) {
          callbacks.onError('Connection lost. Please try again.');
        }
        this.disconnect(connectionKey);
      }
    };

    // Store connection
    this.connections.set(connectionKey, eventSource);

    return eventSource;
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(connectionKey: string): void {
    const eventSource = this.connections.get(connectionKey);
    if (eventSource) {
      console.log(`[CHAT_SSE] Disconnecting: ${connectionKey}`);
      eventSource.close();
      this.connections.delete(connectionKey);
      this.reconnectAttempts.delete(connectionKey);
    }
  }

  /**
   * Disconnect all connections
   */
  disconnectAll(): void {
    console.log(`[CHAT_SSE] Disconnecting all connections (${this.connections.size})`);
    this.connections.forEach((eventSource, key) => {
      eventSource.close();
    });
    this.connections.clear();
    this.reconnectAttempts.clear();
  }
}

// Export singleton instance
export const chatSSEService = new ChatSSEService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    chatSSEService.disconnectAll();
  });
}

